import { useState, useCallback } from "react";
import insuranceService, {
  ExtractedInsuranceData,
} from "../services/insuranceService";

export type ExtractionStep = "idle" | "scanning" | "extracting" | "done" | "error";

interface UseDocumentExtractionReturn {
  step: ExtractionStep;
  progress: number;
  error: string | null;
  extractedData: ExtractedInsuranceData | null;
  extractFromFile: (file: File) => Promise<ExtractedInsuranceData | null>;
  reset: () => void;
}

export function useDocumentExtraction(): UseDocumentExtractionReturn {
  const [step, setStep] = useState<ExtractionStep>("idle");
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [extractedData, setExtractedData] =
    useState<ExtractedInsuranceData | null>(null);

  const extractFromFile = useCallback(
    async (file: File): Promise<ExtractedInsuranceData | null> => {
      setError(null);
      setExtractedData(null);
      setProgress(0);

      try {
        // Upload file to backend for server-side OCR + AI extraction
        setStep("scanning");

        // Simulate progress since we can't track server-side OCR progress
        const progressInterval = setInterval(() => {
          setProgress((prev) => {
            if (prev >= 90) {
              clearInterval(progressInterval);
              return 90;
            }
            return prev + Math.random() * 15;
          });
        }, 500);

        setStep("extracting");
        const result = await insuranceService.extractFromFile(file);

        clearInterval(progressInterval);
        setProgress(100);

        setExtractedData(result);
        setStep("done");
        return result;
      } catch (err: any) {
        const message =
          err?.response?.data?.error ||
          err?.message ||
          "Failed to extract data from the document.";
        setError(message);
        setStep("error");
        return null;
      }
    },
    []
  );

  const reset = useCallback(() => {
    setStep("idle");
    setProgress(0);
    setError(null);
    setExtractedData(null);
  }, []);

  return { step, progress, error, extractedData, extractFromFile, reset };
}
